import { Alert, Button, Drawer, Form, Input, InputNumber, Select } from "antd";
import { useEffect, useState } from "react";
import {
  addDeliveryCharge,
  addExpressDeliveryCharge,
  addOtherCharge,
  addUnloadingCharge,
} from "../../../apis/pro_forma_invoice.api";

// Adds a charge to a pro-forma invoice before the order is placed. No payment is taken here — the
// charge is added to the PI total and paid with the rest of the order.

type ChargeType = "transport" | "unloading" | "express_delivery" | "other";

// Transport and express are both stored as charge_type "Transport Charges"; the remark is how the
// backend tells them apart, so it is fixed rather than user-entered.
const FIXED_REMARKS: Partial<Record<ChargeType, string>> = {
  transport: "Standard Delivery",
  express_delivery: "Express delivery",
};

const hasActiveExpress = (estimate: any) =>
  (estimate?.estimate_additional_charges ?? []).some(
    (charge: any) =>
      charge?.charge_type === "Transport Charges" &&
      String(charge?.remark ?? "").toLowerCase() === "express delivery",
  );

interface AddChargesDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  estimate: any;
}

export function AddChargesDrawer({
  open,
  onClose,
  onSuccess,
  estimate,
}: AddChargesDrawerProps) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const chargeType: ChargeType | undefined = Form.useWatch("charge_type", form);

  const expressActive = hasActiveExpress(estimate);

  const chargeOptions = [
    {
      value: "transport",
      label: "Transport Charges",
      disabled: expressActive,
    },
    { value: "unloading", label: "Unloading Charges" },
    { value: "express_delivery", label: "Express Delivery" },
    { value: "other", label: "Others" },
  ];

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const onFinish = async (values: any) => {
    if (!estimate?.id) return;
    const remark =
      FIXED_REMARKS[values.charge_type as ChargeType] !== undefined
        ? undefined
        : values?.remark?.trim() || undefined;

    setSubmitting(true);
    let result;
    if (values.charge_type === "transport") {
      result = await addDeliveryCharge({
        estimate_id: estimate.id,
        delivery_charge: values.amount,
        operation_type: "add",
      });
    } else if (values.charge_type === "express_delivery") {
      result = await addExpressDeliveryCharge({
        estimate_id: estimate.id,
        express_charge: values.amount,
        operation_type: "add",
      });
    } else if (values.charge_type === "unloading") {
      result = await addUnloadingCharge({
        estimate_id: estimate.id,
        unloading_charge: values.amount,
        operation_type: "add",
        remark,
      });
    } else {
      result = await addOtherCharge({
        estimate_id: estimate.id,
        amount: values.amount,
        remark: remark ?? "",
      });
    }
    setSubmitting(false);

    // The API helpers toast both outcomes; keep the drawer open on failure so the input isn't lost.
    if (result) {
      onSuccess();
      onClose();
    }
  };

  return (
    <Drawer
      title="Add Charges"
      placement="right"
      onClose={onClose}
      open={open}
      width={400}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="charge_type"
          label="Charge"
          rules={[{ required: true, message: "Please select a charge" }]}
        >
          <Select placeholder="Select Charge" options={chargeOptions} />
        </Form.Item>

        {expressActive && (
          <Alert
            className="mb-3"
            type="info"
            showIcon
            message="Express delivery is applied, so transport charges are already covered."
          />
        )}
        {chargeType === "express_delivery" && (
          <Alert
            className="mb-3"
            type="info"
            showIcon
            message="Express delivery replaces any standard transport charge on this PI."
          />
        )}

        <Form.Item
          name="amount"
          label="Amount (incl. 18% GST)"
          rules={[
            { required: true, message: "Please enter the amount" },
            {
              type: "number",
              min: 1,
              message: "Amount must be greater than zero",
            },
          ]}
        >
          <InputNumber
            className="w-100"
            prefix="₹"
            placeholder="Enter amount"
            min={0}
            step={0.01}
          />
        </Form.Item>

        {chargeType && FIXED_REMARKS[chargeType] !== undefined ? (
          <Form.Item label="Remarks">
            <Input value={FIXED_REMARKS[chargeType]} disabled />
          </Form.Item>
        ) : (
          <Form.Item
            name="remark"
            label={chargeType === "other" ? "Remarks" : "Remarks (Optional)"}
            extra={
              chargeType === "other"
                ? "Describe the charge — this is shown against it on the PI."
                : undefined
            }
            rules={[
              {
                required: chargeType === "other",
                whitespace: true,
                message: "Please describe the charge",
              },
            ]}
          >
            <Input.TextArea
              rows={2}
              maxLength={500}
              showCount
              placeholder="Enter remarks"
            />
          </Form.Item>
        )}

        <div className="d-flex justify-content-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Add Charge
          </Button>
        </div>
      </Form>
    </Drawer>
  );
}
